import {
  AmbientLight, Color, DirectionalLight, Group, IcosahedronGeometry, InstancedBufferAttribute,
  InstancedMesh, MeshLambertMaterial, Vector3,
} from 'three';
import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer.js';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { isTouch } from '../_app/cuchillo/core/Basics';
import { Sizes } from '../_app/cuchillo/core/Sizes';
import FluidField from './FluidField';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';

// ═══════════════════════════════════════════════════════════════════════════════════════════
// Posició final de cada partícula (vertex shader):
//
//   position = basePosition + globalDeformation(pos,temps) + floatingOffset(temps) + fluidOffset
//
// globalDeformation i floatingOffset són el moviment BASE (funcions pures del temps, sense estat).
//
// Interacció (2026-10-09): el cursor injecta impuls a un FLUID incompressible 2D (FluidField.js,
// graella fixa, independent de `count`). La incompressibilitat genera desviació lateral i vòrtexs;
// l'advecció fa que la pertorbació viatgi després que el cursor hagi passat; el moment es conserva,
// així que un canvi brusc de direcció crea contracorrent. Cada partícula (GPGPU, O(n)) NEDA dins el
// fluid amb inèrcia (velocitat que persegueix el flux a la seva posició actual), les capes del
// darrere responen més fluix; el desplaçament es DISSIPA exponencialment (no hi ha molla cap a la
// posició original). Calor = velocitat real de la partícula (+ tinta injectada pel cursor).
//
// Responsive: settings SCALE = fracció × Metrics.HEIGHT (veure _computePx()).
// ═══════════════════════════════════════════════════════════════════════════════════════════

const MIN_COUNT = 500;
const MAX_COUNT = 40000;
const FLUID_RES = 128;       // costat de la graella del fluid (cost fix, independent de count)
const FLUID_DOMAIN = 1.5;    // mig costat del domini del fluid = spawnRadius × això (marge perquè el flux rodegi l'esfera)
const MOUSE_SMOOTH = 25;     // 1/s · suavitzat de la velocitat del cursor (treu pics d'un sol frame)
const MOUSE_MAX_SPEED = 6;   // × HEIGHT/s · sostre de velocitat del cursor (salts de posició)
const SPLAT_MIN_SPEED = 0.05; // × HEIGHT/s · per sota d'això el cursor quiet no frena el fluid
const INK_SPEED_REF = 0.5;   // × HEIGHT/s · velocitat del cursor a la qual la tinta injectada és màxima
// Color base vertical: colorBottom sòlid fins a BLEND_START, degradat fins a BLEND_END, colorTop sòlid després.
const BASE_BLEND_START = 0.45;
const BASE_BLEND_END = 0.55;
// Sense ratolí (touch) no hi ha interacció: no es creen ni el fluid ni el GPGPU, i el shader no els mostreja.
const INTERACTIVE = !isTouch;

export const LIQUID_SETTINGS_DESKTOP = {
  spawnRadius: 0.65,      // SCALE · radi de l'esfera
  particleRadius: 0.0025, // SCALE · mida visual de cada partícula
  particleDetail: 2,      // NONE · subdivisió de l'icosaedre de cada partícula (0 = 20 triangles, 2 = 180)
  count: 18000,           // NONE · nº de partícules (sostre MAX_COUNT)
  shellBias: 4.4,         // NONE · 3 = uniforme en volum; >3 buida el centre

  // ── Moviment base ──
  floatAmplitude: 0.004,  // SCALE
  floatSpeed: 1.72,       // NONE
  waveStrength: 0.022,    // SCALE · deformació global
  waveFrequency: 0.0165,  // NONE
  waveSpeed: 0.78,        // NONE
  waveFalloff: 0.2,       // NONE

  // ── Fluid (FluidField.js) ──
  splatRadius: 0.07,        // SCALE · mida de la zona on el cursor injecta energia
  splatForce: 22,           // NONE (1/s) · com de ràpid el fluid local s'apropa a la velocitat del cursor
  splatGain: 2.0,           // NONE · multiplicador de la velocitat injectada (energia per unitat de velocitat)
  velocityDissipation: 0.8, // NONE (1/s) · quant dura el corrent
  vorticity: 22,            // NONE · remolins / desviació lateral
  pressureIterations: 20,   // NONE · abast de la propagació (més = més llunyà)

  // ── Resposta de les partícules al fluid ──
  flowStrength: 0.8,      // NONE · quina fracció de la velocitat del fluid reben les partícules
  coupling: 6,            // NONE (1/s) · com de lligades van al fluid (baix = més inèrcia)
  depthLag: 0.6,          // NONE (0..1) · quant més fluix i tard reaccionen les capes del darrere
  settleRate: 1.0,        // NONE (1/s) · ritme de dissipació del desplaçament (retorn sense molla)
  maxDisplacement: 0.22,  // SCALE · límit suau del desplaçament
  silhouetteLock: 0.85,   // NONE (0..1) · quant es frena el moviment radial cap a fora a la closca

  // ── Calor (velocitat real de la partícula + tinta) ──
  heatSpeedRef: 0.72,     // SCALE (× HEIGHT/s) · velocitat de partícula a la qual la calor = 1
  heatCurve: 2.0,         // NONE · <1 = la calor apareix abans; >1 = només amb moviment fort
  heatDecay: 0.6,         // NONE (1/s) · refredament continu
  inkAmount: 1.0,         // NONE (0..1) · calor que la tinta injectada pel cursor aporta a la zona directa
  inkDissipation: 1.5,    // NONE (1/s) · quant dura la tinta

  // Color base (heat=0) segons aBase.y: colorBottom fins al 45%, degradat 45→55%, colorTop des del 55%.
  colorTop: '#00FFFF',
  colorBottom: '#EEFF00',
  // Degradat de calor: base → colorMid1 → colorMid2 (blanc) → colorMax (rosa).
  colorMid1: '#AFFFB1', // --green-lightest
  colorMid2: '#F9EEDC', // --light
  colorMax: '#F3BACD',  // --pink-light
};

// Mobile (sphereIsMobile()): sense ratolí ni calor (INTERACTIVE), així que el moviment base ha de fer tota la
// feina. Referència 390×844: les fraccions són × HEIGHT, però l'esfera s'ajusta a l'AMPLADA.
export const LIQUID_SETTINGS_MOBILE = {
  ...LIQUID_SETTINGS_DESKTOP,
  spawnRadius: 0.292,     // ≈246px @844 (0.365 × 0.8)
  particleRadius: 0.0025, // ≈2.1px
  count: 6200,
  particleDetail: 0,      // punts de ~2px: 20 triangles n'hi ha prou
  // La resta escalada respecte a la iteració anterior (radi 0.26, 4000 partícules):
  floatAmplitude: 0.003,  // × espaiat entre partícules R/√N (0.26/√4000 → 0.22/√8000 = ×0.6)
  waveStrength: 0.01,     // × radi (×0.85) → ≈8px ≈4.5% del radi
  waveFrequency: 0.047,   // ÷ radi → mateix nº de bonys sobre l'esfera
  waveSpeed: 0.95,
};

export const LIQUID_SETTINGS = { ...LIQUID_SETTINGS_DESKTOP };

// Mobile es decideix en crear l'esfera, NO en carregar el mòdul: a l'emulació mobile de Chrome (DevTools)
// innerWidth val 980/1560 quan s'executen els scripts (el meta viewport s'aplica després) i `isSmartphone`
// de Basics surt false. Mateixa regla que isSmartphone. Es resol un sol cop.
let _isMobile = null;
export function sphereIsMobile() {
  if (_isMobile === null) {
    _isMobile = isTouch && (window.innerWidth <= Sizes.SMARTPHONE || window.innerHeight <= Sizes.SMARTPHONE);
  }
  return _isMobile;
}

// Aplica els overrides de mobile a LIQUID_SETTINGS UN sol cop (si no, cada muntatge de l'esfera
// esborraria el tuning del pane).
let _platformApplied = false;
function applyPlatformSettings() {
  if (_platformApplied) return;
  _platformApplied = true;
  if (sphereIsMobile()) Object.assign(LIQUID_SETTINGS, LIQUID_SETTINGS_MOBILE);
}

// Radi animat en temps d'execució (p.ex. l'scroll del bloc): NO sobreescriu LIQUID_SETTINGS.spawnRadius
// (el valor de repòs del pane). null = es fa servir el de settings.
let _radiusOverride = null;
export function currentSpawnRadius() {
  return _radiusOverride ?? LIQUID_SETTINGS.spawnRadius;
}

// Alçada de referència per als settings SCALE. Desktop: Metrics.HEIGHT. Mobile: alçada del MÓN que veu la càmera (WebGLSketch.size.y), en les mateixes unitats que la posició
// (Scene._placeSphere). A smartphone WebGLSketch no es redimensiona → estable amb la barra d'adreces.
export function sphereHeight() {
  if (!sphereIsMobile()) return Metrics.HEIGHT;
  return WebGLSketch.size?.y || window.innerHeight;
}

// Mateixa fórmula que el vertex shader — exportada perquè Fireflies.js la reutilitzi.
const _heightColorTop = new Color();
export function heightColor(y, spawnRadiusPx, target = new Color()) {
  const yNorm = Math.min(1, Math.max(0, (y / Math.max(spawnRadiusPx, 1e-4)) * 0.5 + 0.5));
  target.set(LIQUID_SETTINGS.colorBottom);
  _heightColorTop.set(LIQUID_SETTINGS.colorTop);
  const t = Math.min(1, Math.max(0, (yNorm - BASE_BLEND_START) / (BASE_BLEND_END - BASE_BLEND_START)));
  return target.lerp(_heightColorTop, t);
}

// Velocitat de cada partícula (px/s, .xyz) + calor (.w).
const VELOCITY_SHADER = `
  uniform sampler2D texBase;
  uniform sampler2D texFluid;
  uniform sampler2D texDye;
  uniform float uFieldExtent;
  uniform float uCellSize;
  uniform float uSpawnRadius;
  uniform float uRadiusScale;
  uniform float uFlowStrength;
  uniform float uCoupling;
  uniform float uDepthLag;
  uniform float uHeatSpeedRef;
  uniform float uHeatCurve;
  uniform float uHeatDecay;
  uniform float uDt;

  void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec3 base = texture2D(texBase, uv).xyz;
    vec4 state = texture2D(textureVelocity, uv);
    vec3 vel = state.xyz;
    float heat = state.w;
    vec3 offset = texture2D(textureOffset, uv).xyz;

    // Es mostreja el fluid a la posició ACTUAL (desplaçada): la partícula és transportada pel corrent.
    vec2 fieldUV = (base.xy * uRadiusScale + offset.xy) / (2.0 * uFieldExtent) + 0.5;
    vec2 flow = texture2D(texFluid, fieldUV).xy * uCellSize * uFlowStrength;

    // Capes del darrere (z<0, lluny de la càmera): menys força i més retard → cisalla en profunditat.
    float front = clamp(base.z / max(uSpawnRadius, 1e-4) * 0.5 + 0.5, 0.0, 1.0);
    float depthW = mix(1.0 - uDepthLag, 1.0, front);

    vec3 flowVel = vec3(flow * depthW, 0.0);
    vel += (flowVel - vel) * (1.0 - exp(-uCoupling * depthW * uDt));

    float speedHeat = pow(clamp(length(vel) / max(uHeatSpeedRef, 1e-4), 0.0, 1.0), uHeatCurve);
    float ink = texture2D(texDye, fieldUV).x * depthW;
    float heatTarget = clamp(max(speedHeat, ink), 0.0, 1.0);
    heat = max(heatTarget, heat * exp(-uHeatDecay * uDt));

    gl_FragColor = vec4(vel, heat);
  }
`;

const OFFSET_SHADER = `
  uniform sampler2D texBase;
  uniform float uSpawnRadius;
  uniform float uSettleRate;
  uniform float uMaxDisplacement;
  uniform float uSilhouetteLock;
  uniform float uDt;

  void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec3 base = texture2D(texBase, uv).xyz;
    vec3 offset = texture2D(textureOffset, uv).xyz;
    vec3 vel = texture2D(textureVelocity, uv).xyz;

    vec3 stepOffset = vel * uDt;

    // Silueta: a la closca, es frena la part del pas que empeny cap a fora.
    vec3 rHat = base / max(length(base), 1e-4);
    float shell = smoothstep(0.55, 1.0, length(base) / max(uSpawnRadius, 1e-4));
    float outward = dot(stepOffset, rHat);
    if (outward > 0.0) stepOffset -= rHat * outward * uSilhouetteLock * shell;

    // Paret suau: com més lluny de la base, menys es deixa créixer el desplaçament.
    if (dot(stepOffset, offset) > 0.0) {
      float room = 1.0 - clamp(length(offset) / max(uMaxDisplacement, 1e-4), 0.0, 1.0);
      stepOffset *= room * room;
    }

    offset += stepOffset;
    offset *= exp(-uSettleRate * uDt);

    gl_FragColor = vec4(offset, 1.0);
  }
`;

function buildMaterial(owner) {
  const material = new MeshLambertMaterial({ color: 0x0c1620 });
  if (INTERACTIVE) material.defines = { INTERACTIVE: '' };
  material.onBeforeCompile = (shader) => {
    // onBeforeCompile és mandrós (1r render): owner._px / _gpuCompute ja estan poblats.
    Object.assign(shader.uniforms, {
      uTime: { value: 0 },
      uParticleRadius: { value: owner._px.particleRadius || 0 },
      uFloatAmplitude: { value: owner._px.floatAmplitude || 0 },
      uMouseOffsetTexture: {
        value: owner._gpuCompute ? owner._gpuCompute.getCurrentRenderTarget(owner._offsetVar).texture : null,
      },
      uHeatTexture: {
        value: owner._gpuCompute ? owner._gpuCompute.getCurrentRenderTarget(owner._velVar).texture : null,
      },
      uSpawnRadius: { value: owner._seedSpawnRadius || 1 },
      uRadiusScale: { value: owner._radiusScale() },
      uWaveStrength: { value: owner._px.waveStrength || 0 },
      uWaveFrequency: { value: LIQUID_SETTINGS.waveFrequency },
      uWaveSpeed: { value: LIQUID_SETTINGS.waveSpeed },
      uWaveFalloff: { value: LIQUID_SETTINGS.waveFalloff },
      uColorTop: { value: new Color(LIQUID_SETTINGS.colorTop) },
      uColorBottom: { value: new Color(LIQUID_SETTINGS.colorBottom) },
      uColorMid1: { value: new Color(LIQUID_SETTINGS.colorMid1) },
      uColorMid2: { value: new Color(LIQUID_SETTINGS.colorMid2) },
      uColorMax: { value: new Color(LIQUID_SETTINGS.colorMax) },
    });

    shader.vertexShader = `
      attribute vec3 aBase, aPhase, aFreq;
      attribute vec2 aUv;
      uniform float uTime, uParticleRadius, uFloatAmplitude, uSpawnRadius, uRadiusScale;
      uniform float uWaveStrength, uWaveFrequency, uWaveSpeed, uWaveFalloff;
      uniform vec3 uColorTop, uColorBottom;
      uniform sampler2D uMouseOffsetTexture, uHeatTexture;
      varying float vHeat;
      varying vec3 vBaseColor;
    ` + shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
      // Deformació global: 3 camps de sin/cos superposats (no radial), amb falloff cap al centre.
      vec3 wp = aBase * uWaveFrequency;
      float wt = uTime * uWaveSpeed;
      float n1 = sin(wp.x * 1.0 + wp.y * 0.6 + wt * 1.10) * cos(wp.z * 0.8 - wt * 0.70);
      float n2 = sin(wp.y * 1.3 - wp.z * 0.9 + wt * 0.85) * cos(wp.x * 0.7 + wt * 1.35);
      float n3 = sin(wp.z * 0.9 + wp.x * 1.4 - wt * 1.45) * cos(wp.y * 1.1 + wt * 0.55);
      vec3 waveDir = vec3(
        n1 - n2 * 0.5 + n3 * 0.25,
        n2 - n3 * 0.5 + n1 * 0.25,
        n3 - n1 * 0.5 + n2 * 0.25
      );
      float rNorm = clamp(length(aBase) / max(uSpawnRadius, 1e-4), 0.0, 1.0);
      vec3 globalDeformation = waveDir * uWaveStrength * pow(rNorm, uWaveFalloff);

      // Flotació individual (fase/freqüència per partícula).
      vec3 floatingOffset = vec3(
        sin(uTime * aFreq.x + aPhase.x),
        sin(uTime * aFreq.y + aPhase.y),
        sin(uTime * aFreq.z + aPhase.z)
      ) * uFloatAmplitude;

      #ifdef INTERACTIVE
        vHeat = texture2D(uHeatTexture, aUv).w;
        vec3 fluidOffset = texture2D(uMouseOffsetTexture, aUv).xyz;
      #else
        vHeat = 0.0;
        vec3 fluidOffset = vec3(0.0);
      #endif

      float yNorm = clamp(aBase.y / max(uSpawnRadius, 1e-4) * 0.5 + 0.5, 0.0, 1.0);
      float baseBlend = clamp((yNorm - ${BASE_BLEND_START.toFixed(4)}) / ${(BASE_BLEND_END - BASE_BLEND_START).toFixed(4)}, 0.0, 1.0);
      vBaseColor = mix(uColorBottom, uColorTop, baseBlend);

      vec3 basePos = aBase * uRadiusScale + globalDeformation + floatingOffset + fluidOffset;
      vec3 transformed = position * uParticleRadius + basePos;
      `
    );

    shader.fragmentShader = `
      uniform vec3 uColorMid1, uColorMid2, uColorMax;
      varying float vHeat;
      varying vec3 vBaseColor;
    ` + shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `
      #include <emissivemap_fragment>
      {
        float h = clamp(vHeat, 0.0, 1.0);
        vec3 rampFull = h < 0.33 ? mix(vBaseColor, uColorMid1, h / 0.33)
                       : h < 0.66 ? mix(uColorMid1, uColorMid2, (h - 0.33) / 0.33)
                       : mix(uColorMid2, uColorMax, (h - 0.66) / 0.34);
        // Intensitat topada a 1.0 a partir del blanc: amb un multiplicador >1 el rosa es retallava a blanc.
        totalEmissiveRadiance += rampFull * (0.15 + min(h / 0.66, 1.0) * 0.85);
      }
      `
    );

    material.userData.shader = shader;
  };
  return material;
}

export default class LiquidSphere {
  constructor() {
    this.group = null;
    this.mesh = null;
    this.geometry = null;
    this.material = null;

    this._count = 0;
    this._seedDetail = -1;
    this._seedSpawnRadius = 0;
    this._seedShellBias = 0;
    this._layer = 0;
    this._time = 0;
    this._px = {};

    this._mouseWorld = new Vector3();
    this._mouseLocal = new Vector3();
    this._prevMouseLocal = new Vector3();
    this._splatFrom = new Vector3();
    this._mouseInit = false;
    this._mouseVelRaw = new Vector3();
    this._mouseVel = new Vector3(); // suavitzada

    this._renderer = null;
    this._gpuCompute = null;
    this._velVar = null;
    this._offsetVar = null;
    this._texSize = 0;
    this._baseArray = null;

    this._fluid = null;
    this._fluidParams = {
      fromX: 0, fromY: 0, toX: 0, toY: 0, radius: 0.05, targetX: 0, targetY: 0, strength: 0, ink: 0,
      vorticity: 0, pressureIterations: 20, velocityDissipation: 0, inkDissipation: 0,
    };
  }

  init(renderer) {
    applyPlatformSettings();
    this._renderer = renderer;

    this._light = new DirectionalLight(0xffffff, 0.6);
    this._light.position.set(1, 1, 0.75).normalize();
    this._ambient = new AmbientLight(0xe8b8ff, 0.35);
    this.group = new Group();
    this.group.add(this._light, this._ambient);

    this.material = buildMaterial(this);
    if (INTERACTIVE) this._fluid = new FluidField(renderer, FLUID_RES);
    this._applyResponsive();

    return this.group;
  }

  _computePx() {
    const s = LIQUID_SETTINGS;
    const H = sphereHeight();
    return {
      particleRadius: H * s.particleRadius,
      spawnRadius: H * currentSpawnRadius(),
      floatAmplitude: H * s.floatAmplitude,
      waveStrength: H * s.waveStrength,
      splatRadius: H * s.splatRadius,
      maxDisplacement: H * s.maxDisplacement,
      heatSpeedRef: H * s.heatSpeedRef,
      fieldExtent: H * currentSpawnRadius() * FLUID_DOMAIN,
    };
  }

  _pushUniforms(px) {
    this._px = px;
    const s = LIQUID_SETTINGS;

    if (this._velVar) {
      const vu = this._velVar.material.uniforms;
      vu.uFieldExtent.value = px.fieldExtent;
      vu.uCellSize.value = (2 * px.fieldExtent) / FLUID_RES;
      vu.uSpawnRadius.value = this._seedSpawnRadius;
      vu.uRadiusScale.value = this._radiusScale();
      vu.uFlowStrength.value = s.flowStrength;
      vu.uCoupling.value = s.coupling;
      vu.uDepthLag.value = s.depthLag;
      vu.uHeatSpeedRef.value = px.heatSpeedRef;
      vu.uHeatCurve.value = s.heatCurve;
      vu.uHeatDecay.value = s.heatDecay;

      const ou = this._offsetVar.material.uniforms;
      ou.uSpawnRadius.value = this._seedSpawnRadius;
      ou.uSettleRate.value = s.settleRate;
      ou.uMaxDisplacement.value = px.maxDisplacement;
      ou.uSilhouetteLock.value = s.silhouetteLock;
    }

    const fp = this._fluidParams;
    fp.radius = px.splatRadius / (2 * px.fieldExtent);
    fp.vorticity = s.vorticity;
    fp.pressureIterations = s.pressureIterations;
    fp.velocityDissipation = s.velocityDissipation;
    fp.inkDissipation = s.inkDissipation;

    const shader = this.material?.userData.shader;
    if (!shader) return;
    shader.uniforms.uParticleRadius.value = px.particleRadius;
    shader.uniforms.uFloatAmplitude.value = px.floatAmplitude;
    shader.uniforms.uSpawnRadius.value = this._seedSpawnRadius;
    shader.uniforms.uRadiusScale.value = this._radiusScale();
    shader.uniforms.uWaveStrength.value = px.waveStrength;
    shader.uniforms.uWaveFrequency.value = s.waveFrequency;
    shader.uniforms.uWaveSpeed.value = s.waveSpeed;
    shader.uniforms.uWaveFalloff.value = s.waveFalloff;
    shader.uniforms.uColorTop.value.set(s.colorTop);
    shader.uniforms.uColorBottom.value.set(s.colorBottom);
    shader.uniforms.uColorMid1.value.set(s.colorMid1);
    shader.uniforms.uColorMid2.value.set(s.colorMid2);
    shader.uniforms.uColorMax.value.set(s.colorMax);
    if (this._gpuCompute) {
      shader.uniforms.uMouseOffsetTexture.value = this._gpuCompute.getCurrentRenderTarget(this._offsetVar).texture;
      shader.uniforms.uHeatTexture.value = this._gpuCompute.getCurrentRenderTarget(this._velVar).texture;
    }
  }

  // Reconstrueix partícules si count/spawnRadius/shellBias/HEIGHT han canviat; si no, només uniforms.
  _applyResponsive() {
    const s = LIQUID_SETTINGS;
    const px = this._computePx();
    const targetCount = Math.max(MIN_COUNT, Math.min(MAX_COUNT, Math.round(s.count)));

    const shellBiasChanged = this._seedShellBias !== s.shellBias;
    // Ni spawnRadius ni HEIGHT reconstrueixen: les basePosition són de la llavor i s'escalen al shader
    // (uRadiusScale). Important a mobile: la barra d'adreces canvia HEIGHT en fer scroll.
    const detailChanged = this._seedDetail !== s.particleDetail;
    if (targetCount !== this._count || shellBiasChanged || detailChanged) {
      this._rebuildParticles(targetCount, px);
      return;
    }

    this._pushUniforms(px);
  }

  // El mesh és nou cada cop → cal re-aplicar la layer (si no, queda a la 0 i desapareix del pass underwater).
  _rebuildParticles(count, px) {
    if (this.mesh && this.group) this.group.remove(this.mesh);
    this.geometry?.dispose();

    this._count = count;
    this._seedDetail = LIQUID_SETTINGS.particleDetail;
    this._seedSpawnRadius = px.spawnRadius;
    this._seedShellBias = LIQUID_SETTINGS.shellBias;
    this._buildMesh(px.spawnRadius);
    if (INTERACTIVE) this._initGpuCompute(this._baseArray);
    if (this.group) this.group.add(this.mesh);
    this.mesh.layers.set(this._layer);

    this._pushUniforms(px);
  }

  // r = spawnRadius × random()^(1/shellBias): shellBias=3 → uniforme en volum, >3 → closca.
  _buildMesh(spawnRadiusPx) {
    this.geometry = new IcosahedronGeometry(1, Math.max(0, Math.round(LIQUID_SETTINGS.particleDetail)));

    const texSize = Math.ceil(Math.sqrt(this._count));
    this._texSize = texSize;

    const shellExponent = 1 / Math.max(0.5, LIQUID_SETTINGS.shellBias);
    const base = new Float32Array(this._count * 3);
    const phase = new Float32Array(this._count * 3);
    const freq = new Float32Array(this._count * 3);
    const uv = new Float32Array(this._count * 2);
    for (let i = 0; i < this._count; i++) {
      const r = spawnRadiusPx * Math.pow(Math.random(), shellExponent);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const i3 = i * 3;
      base[i3] = r * Math.sin(phi) * Math.cos(theta);
      base[i3 + 1] = r * Math.cos(phi);
      base[i3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      for (let a = 0; a < 3; a++) {
        phase[i3 + a] = Math.random() * Math.PI * 2;
        freq[i3 + a] = 0.3 + Math.random() * 0.7;
      }
      const i2 = i * 2;
      uv[i2] = ((i % texSize) + 0.5) / texSize;
      uv[i2 + 1] = (Math.floor(i / texSize) + 0.5) / texSize;
    }
    this.geometry.setAttribute('aBase', new InstancedBufferAttribute(base, 3));
    this.geometry.setAttribute('aPhase', new InstancedBufferAttribute(phase, 3));
    this.geometry.setAttribute('aFreq', new InstancedBufferAttribute(freq, 3));
    this.geometry.setAttribute('aUv', new InstancedBufferAttribute(uv, 2));
    this._baseArray = base;

    this.mesh = new InstancedMesh(this.geometry, this.material, this._count);
    this.mesh.frustumCulled = false;
  }

  _initGpuCompute(base) {
    this._gpuCompute?.dispose();

    const texSize = this._texSize;
    const gpuCompute = new GPUComputationRenderer(texSize, texSize, this._renderer);

    const baseTexture = gpuCompute.createTexture();
    const baseData = baseTexture.image.data;
    for (let i = 0; i < this._count; i++) {
      baseData[i * 4] = base[i * 3];
      baseData[i * 4 + 1] = base[i * 3 + 1];
      baseData[i * 4 + 2] = base[i * 3 + 2];
      baseData[i * 4 + 3] = 1;
    }
    baseTexture.needsUpdate = true;

    const velVar = gpuCompute.addVariable('textureVelocity', VELOCITY_SHADER, gpuCompute.createTexture());
    const offsetVar = gpuCompute.addVariable('textureOffset', OFFSET_SHADER, gpuCompute.createTexture());
    gpuCompute.setVariableDependencies(velVar, [velVar, offsetVar]);
    gpuCompute.setVariableDependencies(offsetVar, [offsetVar, velVar]);

    Object.assign(velVar.material.uniforms, {
      texBase: { value: baseTexture },
      texFluid: { value: this._fluid.velocityTexture },
      texDye: { value: this._fluid.dyeTexture },
      uFieldExtent: { value: 1 },
      uCellSize: { value: 1 },
      uSpawnRadius: { value: 1 },
      uRadiusScale: { value: 1 },
      uFlowStrength: { value: 0 },
      uCoupling: { value: 0 },
      uDepthLag: { value: 0 },
      uHeatSpeedRef: { value: 1 },
      uHeatCurve: { value: 1 },
      uHeatDecay: { value: 0 },
      uDt: { value: 1 / 60 },
    });
    Object.assign(offsetVar.material.uniforms, {
      texBase: { value: baseTexture },
      uSpawnRadius: { value: 1 },
      uSettleRate: { value: 0 },
      uMaxDisplacement: { value: 1 },
      uSilhouetteLock: { value: 0 },
      uDt: { value: 1 / 60 },
    });

    const error = gpuCompute.init();
    if (error !== null) {
      console.error('[LiquidSphere] GPUComputationRenderer init error:', error);
    }

    this._gpuCompute = gpuCompute;
    this._velVar = velVar;
    this._offsetVar = offsetVar;
  }

  loop(dt = 0.016) {
    if (!this.mesh) return;
    dt = Math.min(dt, 1 / 30);
    this._time += dt * LIQUID_SETTINGS.floatSpeed;

    this._updateInteraction(dt);

    const shader = this.material.userData.shader;
    if (shader) shader.uniforms.uTime.value = this._time;
  }

  // Cursor (Interaction + Metrics, camera2D: píxel ↔ món a z=0) → splat al fluid → pas del fluid →
  // pas del GPGPU de partícules.
  _updateInteraction(dt) {
    const shader = this.material.userData.shader;
    if (!shader || !INTERACTIVE) return;

    const s = LIQUID_SETTINGS;
    const H = Metrics.HEIGHT;
    const px = this._px;
    const fp = this._fluidParams;
    const span = 2 * px.fieldExtent;

    this._mouseWorld.set(
      Interaction.positions.mouse.x - Metrics.CENTER_X,
      Metrics.CENTER_Y - Interaction.positions.mouse.y,
      0,
    );
    this.group.updateMatrixWorld();
    this._mouseLocal.copy(this._mouseWorld);
    this.group.worldToLocal(this._mouseLocal);

    if (this._mouseInit) {
      this._mouseVelRaw.copy(this._mouseLocal).sub(this._prevMouseLocal).divideScalar(Math.max(dt, 1e-4));
      const maxSpeed = MOUSE_MAX_SPEED * H;
      if (this._mouseVelRaw.length() > maxSpeed) this._mouseVelRaw.setLength(maxSpeed);
    } else {
      this._mouseInit = true;
      this._prevMouseLocal.copy(this._mouseLocal);
      this._mouseVelRaw.set(0, 0, 0);
    }
    this._mouseVel.lerp(this._mouseVelRaw, 1 - Math.exp(-MOUSE_SMOOTH * dt));
    this._splatFrom.copy(this._prevMouseLocal);
    this._prevMouseLocal.copy(this._mouseLocal);

    const speed = this._mouseVel.length();
    const active = Math.min(1, speed / (SPLAT_MIN_SPEED * H));
    fp.fromX = this._splatFrom.x / span + 0.5;
    fp.fromY = this._splatFrom.y / span + 0.5;
    fp.toX = this._mouseLocal.x / span + 0.5;
    fp.toY = this._mouseLocal.y / span + 0.5;
    fp.targetX = (this._mouseVel.x / span) * FLUID_RES * s.splatGain;
    fp.targetY = (this._mouseVel.y / span) * FLUID_RES * s.splatGain;
    fp.strength = active * (1 - Math.exp(-s.splatForce * dt));
    fp.ink = active * s.inkAmount * Math.min(1, speed / (INK_SPEED_REF * H));
    this._fluid.step(dt, fp);

    if (this._gpuCompute) {
      const vu = this._velVar.material.uniforms;
      vu.texFluid.value = this._fluid.velocityTexture;
      vu.texDye.value = this._fluid.dyeTexture;
      vu.uDt.value = dt;
      this._offsetVar.material.uniforms.uDt.value = dt;

      this._gpuCompute.compute();
      shader.uniforms.uMouseOffsetTexture.value = this._gpuCompute.getCurrentRenderTarget(this._offsetVar).texture;
      shader.uniforms.uHeatTexture.value = this._gpuCompute.getCurrentRenderTarget(this._velVar).texture;
    }
  }

  _radiusScale() {
    return this._seedSpawnRadius > 0 ? (this._px.spawnRadius || this._seedSpawnRadius) / this._seedSpawnRadius : 1;
  }

  // Radi animat (fracció × HEIGHT, com spawnRadius). null → torna al valor de LIQUID_SETTINGS.
  setRadius(value) {
    _radiusOverride = value;
    if (this.material) this._applyResponsive();
  }

  setConfig(partial = {}) {
    Object.assign(LIQUID_SETTINGS, partial);
    if (!this.material) return;
    this._applyResponsive();
  }

  setLayer(n) {
    this._layer = n;
    if (this.group) this.group.traverse((o) => o.layers.set(n));
  }

  resize() { this._applyResponsive(); }

  dispose() {
    this.geometry?.dispose();
    this.material?.dispose();
    this._gpuCompute?.dispose();
    this._gpuCompute = null;
    this._velVar = null;
    this._offsetVar = null;
    this._fluid?.dispose();
    this._fluid = null;
    _radiusOverride = null;
    this.group = null;
    this.mesh = null;
  }

  get settings() { return LIQUID_SETTINGS; }
}
